import React, { useCallback, useMemo, useState, Suspense } from "react";
import { Button, Box } from "@mui/material";
import { DeleteOutline } from "@mui/icons-material";
import { isObject, isEmpty } from "lodash";
import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import Htag from "../typography/Htag";
import LoadingFallback from "../../_src_shared/components/LoadingFallback";
import RffFormChanges from "./RffFormChanges";
import RffForm from "./RffForm";

/**
 * CreateEditForm - Reusable form component for create/edit workflows
 *
 * @param {Object} props
 * @param {Object|null} initialValues - Initial form values (null/empty for create mode)
 * @param {Function} onCreate - Create handler: (values) => void
 * @param {Function} onUpdate - Update handler: (values) => void
 * @param {Function} [onDelete] - Optional delete handler: () => void
 * @param {Function} [onClose] - Optional close handler: () => void
 * @param {ReactNode} children - Form fields/questions to render
 * @param {boolean} [loading] - Loading state for submit buttons
 * @param {boolean} [formSpy] - Enable form spy for debugging
 * @param {string} [createButtonText] - Custom create button text
 * @param {string} [updateButtonText] - Custom update button text
 * @param {string} [deleteButtonText] - Custom delete button text
 * @param {string} [entityName] - Entity name for delete confirmation (e.g., "Credit", "Entity")
 * @param {Function} [transformOnSubmit] - Transform values before submit: (values) => transformedValues
 * @param {Function} [transformOnUpdate] - Transform values before update: (values) => transformedValues
 * @param {boolean} [showDeleteWarning] - Show confirmation modal for delete
 * @param {ReactNode} [footerStartContent] - Additional content at start of footer
 * @param {ReactNode} [footerEndContent] - Additional content at end of footer
 */
const CreateEditForm = ({
  initialValues = null,
  onCreate,
  onUpdate,
  onDelete,
  onClose,
  children,
  loading = false,
  formSpy = false,
  createButtonText = "Create",
  updateButtonText = "Update",
  deleteButtonText = "Delete",
  entityName = "Item",
  transformOnSubmit,
  transformOnUpdate,
  showDeleteWarning = true,
  footerStartContent,
  footerEndContent,
}) => {
  const [showWarning, setShowWarning] = useState(false);

  // Determine if in edit mode
  const isEditMode = useMemo(
    () => isObject(initialValues) && !isEmpty(initialValues),
    [initialValues],
  );

  // Handle create
  const handleCreate = useCallback(
    (values) => {
      const finalValues = transformOnSubmit
        ? transformOnSubmit(values)
        : values;
      onCreate(finalValues);
    },
    [onCreate, transformOnSubmit],
  );

  // Handle update
  const handleUpdate = useCallback(
    (values) => {
      const finalValues = transformOnUpdate
        ? transformOnUpdate(values)
        : values;
      onUpdate(finalValues);
    },
    [onUpdate, transformOnUpdate],
  );

  // Handle delete
  const handleDelete = useCallback(() => {
    if (showDeleteWarning) {
      setShowWarning(true);
    } else {
      onDelete?.();
    }
  }, [onDelete, showDeleteWarning]);

  const handleConfirmDelete = useCallback(() => {
    onDelete?.();
    setShowWarning(false);
  }, [onDelete]);

  // Build footer start content
  const buildFooterStart = useMemo(() => {
    if (footerStartContent) return footerStartContent;

    if (isEditMode && onDelete) {
      return (
        <Button
          size="large"
          sx={{ mr: 1 }}
          color="error"
          variant="outlined"
          endIcon={<DeleteOutline className="thin" />}
          onClick={handleDelete}
        >
          {deleteButtonText}
        </Button>
      );
    }

    return null;
  }, [
    isEditMode,
    onDelete,
    deleteButtonText,
    handleDelete,
    footerStartContent,
  ]);

  // Render delete confirmation overlay
  const renderDeleteWarning = () => {
    if (!showWarning) return null;

    return (
      <Flx
        center
        column
        gap={2}
        sx={{
          py: 4,
          flexGrow: 1,
          height: "100%",
          background: "#ffffffc4",
          position: "absolute",
          top: 0,
          left: 0,
          backdropFilter: "blur(2px)",
          zIndex: 111,
          width: "100%",
        }}
      >
        <Flx column gap={1} ac>
          <Htag variant="h3">Delete {entityName}</Htag>
          <Txt secondary>
            Are you sure you want to delete this {entityName.toLowerCase()}?
          </Txt>
        </Flx>
        <Flx gap={1}>
          <Button
            size="large"
            variant="outlined"
            color="inherit"
            onClick={() => setShowWarning(false)}
          >
            Cancel
          </Button>
          <Button
            loading={loading && showWarning}
            size="large"
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
          >
            Yes, Delete {entityName}
          </Button>
        </Flx>
      </Flx>
    );
  };

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Box sx={{ position: "relative" }}>
        {renderDeleteWarning()}

        {isEditMode ? (
          <RffFormChanges
            submitText={updateButtonText}
            formSpy={formSpy}
            footerStartContent={buildFooterStart}
            footerEndContent={footerEndContent}
            initialValues={initialValues}
            onSubmit={handleUpdate}
            loading={loading}
          >
            {children}
          </RffFormChanges>
        ) : (
          <RffForm
            submitText={createButtonText}
            formSpy={formSpy}
            footerStartContent={buildFooterStart}
            footerEndContent={footerEndContent}
            initialValues={initialValues || {}}
            onSubmit={handleCreate}
            loading={loading}
          >
            {children}
          </RffForm>
        )}
      </Box>
    </Suspense>
  );
};

export default CreateEditForm;
